import { withRouter } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import Alert from "../../../alert";
import bottyCheckingIcon from '../../../../../../../../assets/images/ai-onboarding/botty-checking.svg';
import { Row, Col, Spinner, Button } from "reactstrap";
import ProgressCalculator from "../../../progress-calculator";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory, { 
    PaginationProvider, 
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from 'react-bootstrap-table2-paginator';
import { EMPTY_LIST } from "src/common/constants";
import AIOnboardingService from "src/modules/3rd-party-management/apis/admin/AIOnboardingService";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useEffect } from "react";

const Processing = ({
    t,
    onNextButtonClicked
}) => {
    const {
        data: processingLogs,
        isFetching: logsAreFetching,
        isFetched: logsFetched,
        refetch: refetchProcessingLogs
    } = useQuery({
        queryKey: [
            '3rd-party-management-ai-onboarding-processing-logs'
        ],
        queryFn: async () => {
            const service = AIOnboardingService.getInstance();
            return await service.getLogs();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while retrieving the processing logs.'), {
                type: 'error'
            })
        }
    })

    const {
        data: proccessingStatusData,
        refetch: refetchProcessingStatus
    } = useQuery({
        queryKey: ['3rd-party-management-ai-onboarding-processing-current-state'],
        queryFn: async () => {
            const service = AIOnboardingService.getInstance();

            return await service.getCurrentStep();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching the current status of the operation.'), {
                type: 'error'
            });
        }
    })

    const tableColumns = [
        {
            dataField :   "id",
            text      :   t("SIP ID"),
            sort      :   true,
            key       :   1
        },
        {
            dataField :   "creditor_number",
            text      :   t("Creditor Number"),
            sort      :   true,
            key       :   2
        },
        {
            dataField :   "company_name",
            text      :   t("Company"),
            sort      :   false,
            key       :   3
        },
        {
            dataField :   "country",
            text      :   t("Country"),
            sort      :   true,
            key       :   4
        },
        {
            dataField :   "vat_id",
            text      :   t("VAT ID"),
            sort      :   true,
            key       :   5
        },
        {
            dataField :   "financial_year",
            text      :   t("Financial Year"),
            sort      :   false,
            key       :   6
        },
        {
            dataField :   "description",
            text      :   t("Error"),
            sort      :   false,
            key       :   7,
            formatter :   (cellContent) => {
                return (
                    <p className="text-danger">
                        {cellContent}
                    </p>
                )
            }
        },
    ]

    const NoDataIndication = () => (
        (logsFetched && !processingLogs?.length) ? <div className="alert m-0" role="alert">
            <p style={{
                    textAlign: "center",
                    marginBottom: 0
                }}>
                {t(EMPTY_LIST)}
            </p>
        </div> : <></>
    )

    useEffect(() => {
        const fetchStatusInterval = setInterval(() => {
            refetchProcessingStatus()
            refetchProcessingLogs()
        }, 15000)

        return () => {
            clearInterval(fetchStatusInterval)
        }
    }, [])
    
    const totalRecordsCount = parseInt(proccessingStatusData?.recordsCount || 0)
    const processedCount = parseInt(proccessingStatusData?.processedCount || 0)
    const rejectedCount = parseInt(proccessingStatusData?.rejectedCount || 0)
    const waitingCount = parseInt(proccessingStatusData?.waitingCount || 0)

    const nextButtonIsDisabled = !proccessingStatusData?.status || proccessingStatusData?.status !== 'done_process'

    return (
        <>
            <Alert 
                icon={bottyCheckingIcon}
                title='Import In Progress'
                message='The import has started and is being processed in the background. You can continue working — the latest status will be shown when you return.'
            />

            <div className="ai-onboarding-content-body">
                <Row>
                    <Col sm='12' className="mt-2 mb-4">
                        <ProgressCalculator
                            totalCount={totalRecordsCount}
                            processedCount={processedCount} 
                            rejectedCount={rejectedCount} 
                            waitingCount={waitingCount} 
                        />
                    </Col>

                    <Col sm='12'>
                        <PaginationProvider
                            pagination={
                                paginationFactory({
                                    custom              :   true,
                                    page                :   1,
                                    sizePerPage         :   25,
                                    totalSize           :   processingLogs?.length || 0,
                                    withFirstAndLast    :   true,
                                    alwaysShowAllBtns   :   true,
                                    prePageText         :   <span><i className="ri-arrow-left-s-line"></i> {t('Back')}</span>,
                                    nextPageText        :   <span>{t('Next')} <i className="ri-arrow-right-s-line"></i></span>,
                                    prePageTitle        :   t('Pre page'),
                                    firstPageTitle      :   t('First page'),
                                    lastPageTitle       :   t('Last page'),
                                    showTotal           :   false,
                                    paginationTotalRenderer :   (from, to, size) => {
                                        return (
                                            <span className="react-bootstrap-table-pagination-total">
                                                {`${t('Total Items')} ${size}`}
                                            </span>
                                        )
                                    },
                                    hideSizePerPage     :   false,
                                    sizePerPageList     :   [
                                        {
                                            text: '25', 
                                            value: 25
                                        }, 
                                        {
                                            text: '50', 
                                            value: 50
                                        },
                                        {
                                            text: '100', 
                                            value: 100
                                        }
                                    ]
                                })
                            }>
                            {
                                ({
                                    paginationProps,
                                    paginationTableProps
                                }) => (
                                    <>
                                        <Row>
                                            <Col sm="12">
                                                <h5 className="mt-4">{t('Import Log')} – {t('Failed Entries')}</h5>
                                                <p className="text-muted">
                                                    {t('Only entries that could not be imported are shown below.')}
                                                </p>
                                                <BootstrapTable
                                                    loading={ logsAreFetching }
                                                    overlay={ overlayFactory({ spinner: <Spinner animation="border" variant="primary" size="md"/>, text : `${t("Loading")}...` }) }
                                                    defaultSorted={[]}
                                                    keyField={"id"}
                                                    responsive
                                                    bordered={ false }
                                                    data={ processingLogs || [] }
                                                    striped={true}
                                                    columns={tableColumns}
                                                    wrapperClasses="table-responsive"
                                                    classes={"table tpdd-table"}
                                                    headerWrapperClasses={"thead-light"}
                                                    noDataIndication={ () => <NoDataIndication /> }
                                                    { ...paginationTableProps }
                                                />
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Col sm="12">
                                                <div className="tpdd-pagination-style-1">
                                                    <PaginationTotalStandalone
                                                        { ...paginationProps }
                                                    />
                                                    
                                                    <div>
                                                        <PaginationListStandalone
                                                            { ...paginationProps }
                                                        />
                                                        
                                                        <SizePerPageDropdownStandalone
                                                            { ...paginationProps }
                                                        />
                                                    </div>
                                                </div>
                                            </Col>
                                        </Row>
                                    </>
                                )
                            }
                        </PaginationProvider>
                    </Col>
                </Row>

                <div className="ai-onboarding-tab-actions">
                    <Button 
                        disabled={nextButtonIsDisabled}
                        color="primary" 
                        onClick={onNextButtonClicked}
                    >
                        {t('Next')}
                    </Button>
                </div>
            </div>
        </>
    )
}

const mapStatetoProps = (state) => {
    const { token } = state.Login;
    return {
        user          :   state.Login.user,
        token         :   token
    };
};

export default withNamespaces()(
    withRouter(connect(
        mapStatetoProps,
        {}
    )(Processing))
)